import { Command } from "commander";
import chalk from "chalk";
import os from "os";
import path from "path";
import fs from "fs";

import { checkJavaDiagnostic } from "../diagnostics/javaDiagnostic.js";
import { checkAndroidDiagnostic } from "../diagnostics/androidDiagnostic.js";
import { checkIosDiagnostic } from "../diagnostics/iosDiagnostic.js";
import { checkReactNativeDiagnostic } from "../diagnostics/reactNativeDiagnostic.js";
import { checkDependencyDiagnostic } from "../diagnostics/dependencyDiagnostic.js";
import { Diagnostic } from "../models/diagnostic.js";
import { loadPluginsForProject } from "../plugins/loader.js";
import { generateDiagnosticBundle } from "../diagnostics/bundleGenerator.js";

import { getJavaVersion, runCommand } from "../utils/exec.js";
import { findPackageJson, safeReadJson, directoryExists } from "../utils/files.js";

export const explainCommand = new Command("explain")
  .description("Analyze React Native project and explain likely causes of failures")
  .option("--json", "Output diagnostics in JSON format")
  .action(async (options: { json?: boolean }) => {
    try {
      // 1. Gather all configuration parameters
      const packageJsonPath = findPackageJson(process.cwd());
      const hasPackageJson = !!packageJsonPath;
      let projectRoot = process.cwd();
      let reactNativeInstalled = false;
      let reactNativeVersion: string | null = null;
      let iosDirExists = false;
      let androidDirExists = false;
      let dependencies: Record<string, string> = {};
      let devDependencies: Record<string, string> = {};

      if (hasPackageJson) {
        projectRoot = path.dirname(packageJsonPath);
        const pkg = safeReadJson(packageJsonPath);
        if (pkg) {
          dependencies = (pkg["dependencies"] as Record<string, string>) || {};
          devDependencies = (pkg["devDependencies"] as Record<string, string>) || {};
          const rnVersion = dependencies["react-native"] || devDependencies["react-native"];
          if (rnVersion) {
            reactNativeInstalled = true;
            reactNativeVersion = rnVersion.replace(/^[\^~>=<]+/, "");
          }
        }
        iosDirExists = directoryExists(path.join(projectRoot, "ios"));
        androidDirExists = directoryExists(path.join(projectRoot, "android"));
      }

      // Environmental details
      const javaVersion = await getJavaVersion();
      const androidHome = process.env["ANDROID_HOME"];
      const sdkRoot = process.env["ANDROID_SDK_ROOT"];
      const adbRes = await runCommand("adb --version");
      let adbAvailable = !adbRes.error;

      // adb fallback check
      if (!adbAvailable && androidHome && directoryExists(androidHome)) {
        const adbBinaryName = os.platform() === "win32" ? "adb.exe" : "adb";
        const fallbackPath = path.join(androidHome, "platform-tools", adbBinaryName);
        if (fs.existsSync(fallbackPath)) {
          adbAvailable = true;
        }
      }

      // macOS only configurations
      const isMac = os.platform() === "darwin";
      let xcodebuildAvailable = false;
      let podAvailable = false;
      if (isMac) {
        const xcodeRes = await runCommand("xcodebuild -version");
        xcodebuildAvailable = !xcodeRes.error;
        const podRes = await runCommand("pod --version");
        podAvailable = !podRes.error;
      }

      // Load plugin diagnostics rules dynamically
      const pluginDiagnostics: Diagnostic[] = [];
      try {
        const loadedPlugins = await loadPluginsForProject(projectRoot);
        for (const plugin of loadedPlugins) {
          if (plugin.analyzeRules) {
            const diags = await plugin.analyzeRules(projectRoot);
            pluginDiagnostics.push(...diags);
          }
        }
      } catch {}

      // 2. Execute rules
      const diagnostics: Diagnostic[] = [
        ...checkReactNativeDiagnostic(hasPackageJson, reactNativeInstalled, iosDirExists, androidDirExists),
        ...checkJavaDiagnostic(javaVersion, reactNativeVersion),
        ...checkAndroidDiagnostic(androidHome, sdkRoot, adbAvailable),
        ...checkIosDiagnostic(isMac, xcodebuildAvailable, podAvailable),
        ...checkDependencyDiagnostic(dependencies, devDependencies),
        ...pluginDiagnostics,
      ];

      // 3. Score Health
      const score = calculateScore(diagnostics, hasPackageJson);

      // 4. Output results based on formatting
      if (options.json) {
        console.log(JSON.stringify({ score, diagnostics }, null, 2));
        process.exit(diagnostics.some((d) => d.severity === "error") ? 1 : 0);
        return;
      }

    console.log();
    console.log(chalk.bold.underline("React Native Project Health Diagnostics"));
    console.log();

    let scoreColor = chalk.green;
    if (score < 50) {
      scoreColor = chalk.red;
    } else if (score < 80) {
      scoreColor = chalk.yellow;
    }
    console.log(`Health Score: ${scoreColor(score)}/100`);
    console.log();

    if (diagnostics.length === 0) {
      console.log(chalk.green("✓ No problems detected! Your setup is healthy."));
      console.log();
      process.exit(0);
      return;
    }

    // Grouping: errors first, warnings second, information last
    const errors = diagnostics.filter((d) => d.severity === "error");
    const warnings = diagnostics.filter((d) => d.severity === "warning");
    const infos = diagnostics.filter((d) => d.severity === "info");
    const sortedDiags = [...errors, ...warnings, ...infos];

    console.log(chalk.bold("Potential Problems Detected"));
    console.log();

    sortedDiags.forEach((diag, index) => {
      let severityLabel = "";
      if (diag.severity === "error") {
        severityLabel = chalk.bold.red("[Error]");
      } else if (diag.severity === "warning") {
        severityLabel = chalk.bold.yellow("[Warning]");
      } else {
        severityLabel = chalk.bold.cyan("[Info]");
      }

      console.log(`${index + 1}. ${severityLabel} ${chalk.bold(diag.title)}`);
      console.log();
      console.log(`${chalk.dim("Detected:")}\n  ${diag.detected}`);
      console.log();
      console.log(`${chalk.dim("Why this matters:")}\n  ${diag.explanation}`);
      console.log();

      if (diag.symptoms.length > 0) {
        console.log(chalk.dim("Likely symptoms:"));
        diag.symptoms.forEach((sym) => console.log(`  * ${sym}`));
        console.log();
      }

      if (diag.fixes.length > 0) {
        console.log(chalk.dim("Suggested fix:"));
        diag.fixes.forEach((fix) => console.log(`  * ${fix}`));
        console.log();
      }

      if (diag.documentationUrl) {
        console.log(`${chalk.dim("Documentation:")}\n  ${chalk.blue.underline(diag.documentationUrl)}`);
        console.log();
      }

      console.log(chalk.dim("─".repeat(60)));
      console.log();
    });

      process.exit(errors.length > 0 ? 1 : 0);
    } catch (err: any) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      const reportPath = await generateDiagnosticBundle("explain", errorObj);
      console.error(chalk.red(`Error: ${errorObj.message}`));
      console.error(chalk.yellow(`Diagnostic bundle generated: ${reportPath}`));
      process.exit(1);
    }
  });

export function calculateScore(diagnostics: Diagnostic[], hasPackageJson: boolean): number {
  let score = 100;
  for (const diag of diagnostics) {
    if (diag.severity === "error") {
      score -= 20;
    } else if (diag.severity === "warning") {
      score -= 10;
    }
  }
  score = Math.max(0, Math.min(100, score));

  if (!hasPackageJson) {
    score = Math.min(30, score);
  }

  return score;
}
